import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, logDev } from "../api";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import {
  ShieldCheck, ShieldAlert, ShieldX, Calendar, Award,
  ArrowUpRight, Copy, CheckCheck, Linkedin, Share2,
} from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return s; }
}

export default function VerifyCert() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, cert: null, error: null });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setState({ loading: true, cert: null, error: null });
    api.get(`/lms/certificates/${encodeURIComponent(certId)}`)
      .then(r => { if (alive) setState({ loading: false, cert: r.data, error: null }); })
      .catch(e => {
        if (!alive) return;
        const status = e?.response?.status;
        setState({
          loading: false,
          cert: null,
          error: status === 404 ? "not_found" : "unknown",
        });
      });
    return () => { alive = false; };
  }, [certId]);

  // SEO + schema.org tags — injected only when we have a verified credential.
  useEffect(() => {
    if (!state.cert) return;
    const cert = state.cert;
    const url = window.location.href;
    document.title = `Verified Credential · ${cert.lab_code || cert.cert_no} · SkillConnect`;
    const setMeta = (name, content, attr = "name") => {
      let m = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute(attr, name); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    setMeta("description",
      `Verified SkillConnect credential ${cert.cert_no} — ${cert.lab_name || cert.lab_code} issued to ${cert.student_name || "candidate"}. Tamper-proof, public verification.`);
    setMeta("robots", "index,follow,max-image-preview:large");
    setMeta("og:title", `Verified · ${cert.lab_name || cert.lab_code}`, "property");
    setMeta("og:description",
      `Issued ${fmtDate(cert.issued_at)} to ${cert.student_name || "candidate"} · Credential ${cert.cert_no}.`, "property");
    setMeta("og:type", "profile", "property");
    setMeta("og:url", url, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", `Verified · ${cert.lab_name || cert.lab_code}`);

    // JSON-LD EducationalOccupationalCredential schema
    const old = document.getElementById("verify-jsonld");
    if (old) old.remove();
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "EducationalOccupationalCredential",
      "name": cert.lab_name || cert.lab_code || "SkillConnect Verified Credential",
      "credentialCategory": "Certificate",
      "url": url,
      "identifier": cert.cert_no,
      "dateCreated": cert.issued_at,
      "recognizedBy": {
        "@type": "Organization",
        "name": "SkillConnect",
        "url": "https://skillconnect.com",
        "logo": "https://skillconnect.com/brand/skillconnect-logo.png",
      },
      "about": {
        "@type": "DefinedTerm",
        "name": cert.lab_name || cert.lab_code,
        "termCode": cert.lab_code,
      },
      ...(cert.student_name ? {
        "credentialSubject": {
          "@type": "Person",
          "name": cert.student_name,
        }
      } : {}),
      "validFrom": cert.issued_at,
    };
    const tag = document.createElement("script");
    tag.id = "verify-jsonld";
    tag.type = "application/ld+json";
    tag.textContent = JSON.stringify(jsonld);
    document.head.appendChild(tag);

    return () => {
      const t = document.getElementById("verify-jsonld");
      if (t) t.remove();
    };
  }, [state.cert]);

  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) { logDev.warn("[verify-cert] clipboard blocked:", err); }
  };

  const shareLinkedIn = (cert) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(`SkillConnect — ${cert.lab_code}`);
    const summary = encodeURIComponent(
      `Verified credential ${cert.cert_no} issued by SkillConnect for ${cert.lab_name}.`
    );
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}&summary=${summary}`,
      "_blank", "noopener,noreferrer"
    );
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <PublicNavBar />

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <span className="eyebrow" style={{ color: "var(--signal)" }}>Credential verification</span>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">
            Verify a SkillConnect credential
          </h1>
          <p className="text-muted mt-2 max-w-xl mx-auto text-sm">
            Anyone — recruiters, registrars, anyone — can verify a SkillConnect-issued
            certificate by visiting this link. Live look-up from the issuing database.
          </p>
        </div>

        {state.loading && (
          <div className="card card-pad text-center" data-testid="verify-loading">
            <div className="animate-pulse text-muted">Looking up credential…</div>
          </div>
        )}

        {!state.loading && state.error === "not_found" && (
          <div className="card card-pad text-center border-2"
            style={{ borderColor: "#DC2626", background: "rgba(220,38,38,0.04)" }}
            data-testid="verify-not-found">
            <ShieldX size={42} className="mx-auto" style={{ color: "#DC2626" }} />
            <h2 className="font-display font-black text-2xl mt-3" style={{ color: "#DC2626" }}>
              No matching credential
            </h2>
            <p className="text-sm text-muted mt-2">
              We couldn&apos;t find a certificate with this ID:
              <code className="ml-1 px-1.5 py-0.5 rounded text-[12px]" style={{ background: "var(--surface-2,#F1F5F9)" }}>
                {certId}
              </code>
            </p>
            <p className="text-xs text-muted mt-4">
              If you received this link from a candidate, the credential may have been revoked,
              entered incorrectly, or never existed.
            </p>
            <Link to="/" className="btn-ghost mt-5 inline-flex">Back to SkillConnect</Link>
          </div>
        )}

        {!state.loading && state.error === "unknown" && (
          <div className="card card-pad text-center">
            <ShieldAlert size={32} className="mx-auto" style={{ color: "#0099FF" }} />
            <div className="font-semibold mt-2">Verification temporarily unavailable</div>
            <div className="text-sm text-muted mt-1">Please try again in a moment.</div>
          </div>
        )}

        {!state.loading && state.cert && (
          <>
            {/* Verified hero */}
            <div className="rounded-2xl border bg-white shadow-lg overflow-hidden"
              style={{ borderColor: "var(--rule)" }}
              data-testid="verify-success">
              {/* coloured top stripe */}
              <div style={{ height: 6, background: state.cert.lab_color || "#006CFF" }} />

              <div className="p-7 md:p-9">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest text-white"
                      style={{ background: "var(--signal)" }}>
                      <ShieldCheck size={13} /> Verified
                    </div>
                    <h2 className="font-display font-black text-2xl md:text-3xl mt-3 leading-tight">
                      {state.cert.student_name || "Student"}
                    </h2>
                    <p className="text-muted text-sm mt-1">
                      has earned the SkillConnect credential below.
                    </p>
                  </div>

                  <img
                    src="/brand/skillconnect-logo.png?v=8"
                    alt="SkillConnect"
                    className="h-9 w-auto object-contain shrink-0"
                    draggable="false"
                  />
                </div>

                {/* Credential body */}
                <div className="mt-7 grid sm:grid-cols-2 gap-5">
                  <div className="rounded-xl p-5 border" style={{ borderColor: "var(--rule)", background: "var(--paper)" }}>
                    <div className="text-xs uppercase tracking-widest font-bold" style={{ color: state.cert.lab_color || "#006CFF" }}>
                      {state.cert.lab_code}
                    </div>
                    <div className="font-display font-bold text-lg mt-1">{state.cert.lab_name}</div>
                    <div className="text-xs text-muted mt-2 inline-flex items-center gap-1.5">
                      <Award size={12} /> Center of Excellence Lab
                    </div>
                  </div>

                  <div className="rounded-xl p-5 border" style={{ borderColor: "var(--rule)", background: "var(--paper)" }}>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted">Issue date</div>
                    <div className="font-display font-bold text-lg mt-1 inline-flex items-center gap-2">
                      <Calendar size={16} style={{ color: "var(--signal)" }} />
                      {fmtDate(state.cert.issued_at)}
                    </div>
                    <div className="text-xs text-muted mt-2">Issued by SkillConnect (Tech Catalyst Pvt. Ltd.)</div>
                  </div>
                </div>

                {/* Credential number */}
                <div className="mt-5 rounded-xl border p-4 flex items-center justify-between gap-3 flex-wrap"
                  style={{ borderColor: "var(--rule)", background: "var(--paper)" }}>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted">Credential number</div>
                    <code className="block font-mono font-bold text-base mt-1 break-all" data-testid="verify-cert-no">
                      {state.cert.cert_no}
                    </code>
                  </div>
                  <button onClick={() => copy(state.cert.cert_no)} className="btn-ghost text-xs"
                    data-testid="verify-copy">
                    {copied ? <><CheckCheck size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                  </button>
                </div>

                {/* Share */}
                <div className="mt-6 flex flex-wrap gap-2">
                  <button onClick={() => shareLinkedIn(state.cert)} className="btn-secondary text-sm"
                    data-testid="verify-share-linkedin">
                    <Linkedin size={14} /> Share on LinkedIn
                  </button>
                  <button onClick={() => copy(window.location.href)} className="btn-ghost text-sm"
                    data-testid="verify-copy-link">
                    <Share2 size={14} /> Copy verify link
                  </button>
                  <Link to="/lms" className="btn-ghost text-sm ml-auto">
                    Explore CoE Labs <ArrowUpRight size={13} />
                  </Link>
                </div>
              </div>

              {/* Footer band */}
              <div className="px-7 md:px-9 py-4 border-t text-[11px] font-mono uppercase tracking-widest text-muted flex items-center justify-between gap-3 flex-wrap"
                style={{ borderColor: "var(--rule)", background: "var(--surface-2,#F1F5F9)" }}>
                <span>This credential is verified live against SkillConnect&apos;s issuing database.</span>
                <span>skillconnect.com/verify</span>
              </div>
            </div>

            <p className="text-center text-xs text-muted mt-6">
              Spotted something wrong? <a href="mailto:info@skillconnect.com" className="underline hover:text-signal">Report this credential</a>.
            </p>
          </>
        )}
      </main>

      <PublicFooter compact />
    </div>
  );
}
